import { useEffect, useMemo, useState } from 'react';
import { GLOSSARY_LIST } from '../content/glossary';
import { LESSON_BY_ID, LESSONS } from '../content/lessons';
import { PRESETS } from '../content/presets';
import type { ControlKey } from '../content/types';
import { LessonView } from '../components/lesson/LessonView';
import { parseMarkup } from '../components/Markup';
import { MathTex } from '../components/MathTex';
import { SimulatorPanel, statusText, type SimMode } from '../components/sim/SimulatorPanel';
import { useSim } from '../components/sim/useSim';
import { TermPopover } from '../components/Term';
import { downloadWorkPdf } from '../state/exportWorkPdf';
import { lessonStatuses, sandboxUnlocked } from '../state/progress';
import { useApp } from '../state/store';
import { Header } from './Header';
import { lessonPath, navigate, useRoute } from './router';
import { SplitView } from './SplitView';

const ALL_KINDS = ['manual', 'open-loop', 'bang-bang', 'p', 'pi', 'pid'] as const;

export function App() {
  const route = useRoute();
  const progress = useApp((s) => s.progress);
  const currentLessonId = useApp((s) => s.currentLessonId);
  const setCurrentLesson = useApp((s) => s.setCurrentLesson);
  const statuses = useMemo(() => lessonStatuses(progress), [progress]);
  const sandboxOpen = sandboxUnlocked(progress);

  // Home goes to the furthest unlocked lesson the student was on.
  useEffect(() => {
    if (route.name === 'home') {
      const st = statuses.find((s) => s.lesson.id === currentLessonId);
      const target = st?.unlocked ? currentLessonId : ([...statuses].reverse().find((s) => s.unlocked)?.lesson.id ?? 'm0');
      navigate(lessonPath(target), { replace: true });
    }
  }, [route, statuses, currentLessonId]);

  const lessonStatus = route.name === 'lesson' ? statuses.find((s) => s.lesson.id === route.id) : undefined;
  const lessonId = lessonStatus?.unlocked ? lessonStatus.lesson.id : null;
  useEffect(() => {
    if (lessonId) setCurrentLesson(lessonId);
  }, [lessonId, setCurrentLesson]);

  useEffect(() => {
    const title =
      route.name === 'lesson' && lessonStatus ? `Module ${lessonStatus.lesson.number}: ${lessonStatus.lesson.title}` : route.name === 'sandbox' ? 'Summary' : route.name === 'glossary' ? 'Glossary' : '';
    document.title = title ? `${title} · Altitude Control Lab` : 'Altitude Control Lab';
  }, [route, lessonStatus]);

  let left: React.ReactNode;
  let mode: SimMode;
  if (route.name === 'lesson' && lessonStatus?.unlocked) {
    const idx = lessonStatus.lesson.number;
    const controls = new Set<ControlKey | 'all'>(LESSONS.slice(0, idx + 1).flatMap((l) => l.controls));
    mode = { controls, kinds: [...lessonStatus.lesson.controllerKinds] };
    left = <LessonView lesson={lessonStatus.lesson} statuses={statuses} />;
  } else if (route.name === 'lesson' && lessonStatus) {
    mode = { controls: new Set(), kinds: [] };
    left = <Locked reason={lessonStatus.lockReason ?? 'This module is locked.'} statuses={statuses} />;
  } else if (route.name === 'sandbox') {
    mode = sandboxOpen ? { controls: new Set(['all']), kinds: [...ALL_KINDS] } : { controls: new Set(), kinds: [] };
    left = sandboxOpen ? <Sandbox /> : <Locked reason="The summary opens after you pass the capstone check in Module 8." statuses={statuses} />;
  } else if (route.name === 'glossary') {
    mode = { controls: new Set(), kinds: [] };
    left = <Glossary />;
  } else if (route.name === 'lesson' || route.name === 'not-found') {
    mode = { controls: new Set(), kinds: [] };
    left = <NotFound />;
  } else {
    mode = { controls: new Set(), kinds: [] };
    left = null;
  }

  return (
    <>
      <Header statuses={statuses} route={route} sandboxOpen={sandboxOpen} />
      <Notices />
      <main id="main" tabIndex={-1}>
        <SplitView left={left} right={<SimulatorPanel mode={mode} />} status={<NarrowStatus />} />
      </main>
      <TermPopover />
    </>
  );
}

function NarrowStatus() {
  const sim = useSim();
  const exp = useApp((s) => s.experiment);
  return (
    <span className="small" role="status">
      <strong>{exp ? PRESETS[exp.presetId]?.title : ''}</strong> · {statusText(sim.status, sim.playing, sim.t, sim.stopReason?.message ?? null)}
    </span>
  );
}

function Notices() {
  const notices = useApp((s) => s.notices);
  const dismiss = useApp((s) => s.dismiss);
  const storageStatus = useApp((s) => s.storageStatus);
  const storageMessage = useApp((s) => s.storageMessage);
  return (
    <div className="notices" aria-live="polite">
      {(storageStatus === 'failed' || storageStatus === 'unavailable' || storageStatus === 'trimmed') && storageMessage && (
        <div className={`notice ${storageStatus === 'trimmed' ? 'info' : 'warn'}`}>{storageMessage}</div>
      )}
      {notices.map((n) => (
        <div key={n.id} className={`notice ${n.tone}`} role={n.tone === 'error' ? 'alert' : undefined}>
          <span>{parseMarkup(n.text, `n${n.id}`)}</span>
          <button type="button" className="icon-btn" onClick={() => dismiss(n.id)} aria-label="Dismiss message">
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}

function Locked({ reason, statuses }: { reason: string; statuses: ReturnType<typeof lessonStatuses> }) {
  const furthest = [...statuses].reverse().find((s) => s.unlocked)!;
  const st = furthest;
  return (
    <div className="lesson">
      <h1>Locked</h1>
      <p>{reason}</p>
      <p>
        You are working on <strong>Module {st.lesson.number}: {st.lesson.title}</strong>.{' '}
        {st.labComplete ? `The check has ${st.itemsRemaining} question(s) left.` : `The lab still needs: ${st.missingSteps.map((s) => s.title).join('; ')}.`}
      </p>
      <button type="button" className="btn primary" onClick={() => navigate(lessonPath(st.lesson.id))}>
        Go to Module {st.lesson.number}
      </button>
    </div>
  );
}

function NotFound() {
  return (
    <div className="lesson">
      <h1>Page not found</h1>
      <p>That address does not match a module.</p>
      <button type="button" className="btn primary" onClick={() => navigate('/')}>
        Go to my current module
      </button>
    </div>
  );
}

function Sandbox() {
  const loadPreset = useApp((s) => s.loadPreset);
  const current = useApp((s) => s.experiment?.presetId);
  const progress = useApp((s) => s.progress);
  const runs = useApp((s) => s.runs);
  const predictions = useApp((s) => s.predictions);
  const reflections = useApp((s) => s.reflections);
  const notify = useApp((s) => s.notify);
  const [studentId, setStudentId] = useState('');
  return (
    <div className="lesson">
      <p className="eyebrow">Course complete</p>
      <h1>Summary</h1>
      <p className="lead">
        Free practice mode. You can use every controller type and every setting. Pick a starting scenario below, change whatever you like on the
        right, run the sim, and save runs so you can compare them.
      </p>
      <ul className="preset-list">
        {Object.values(PRESETS).map((p) => (
          <li key={p.id}>
            <button type="button" className={`btn${current === p.id ? ' primary' : ''}`} onClick={() => loadPreset(p.id)}>
              {p.title}
            </button>
            <span className="small muted"> {p.description}</span>
          </li>
        ))}
      </ul>
      <h2>Review</h2>
      <ul>
        {LESSONS.map((l) => (
          <li key={l.id}>
            <a href={lessonPath(l.id)} onClick={(e) => (e.preventDefault(), navigate(lessonPath(l.id)))}>
              Module {l.number}: {l.title}
            </a>
          </li>
        ))}
      </ul>
      <h3>Practical PID tuning</h3>
      <p>
        First, tune P for responsiveness (look for a fast initial rise on the graph). Next, add D to suppress overshoot and oscillations (look for flattened peaks and a smooth arrival at the target). Finally, add I to eliminate steady-state sag (look for the trace closing any persistent gap after a load is applied).
      </p>
      <h2>Ideas to explore</h2>
      <ul>
        <li>
          <strong>Test prediction vs. correction:</strong> Lie to the feedforward system about the drone's weight. See how much harder the feedback system has to work to fix the bad guess and maintain a hover (from the <em>Predict versus correct</em> module).
        </li>
        <li>
          <strong>Tame sensor noise:</strong> Turn on noise with a strong derivative (D) gain and watch the motors chatter wildly. Then, increase the filter time to smooth out the thrust (from the <em>PID control</em> module).
        </li>
        <li>
          <strong>Push it to the limit:</strong> Ask the untethered drone to fly to a target it physically can't reach. Turn off anti-windup to see it build up "remembered error" and overcompensate, then turn it on to see how it safely recovers (from the <em>Integral control</em> module).
        </li>
      </ul>
      <div className="sandbox-turnin">
        <h2>Download a PDF report of your work.</h2>
        <label className="field">
          <span className="field-label">Name or Student ID</span>
          <input
            type="text"
            value={studentId}
            onChange={(e) => setStudentId(e.target.value)}
            autoComplete="username"
            placeholder="Enter your name or student ID"
          />
        </label>
        <button
          type="button"
          className="btn primary"
          onClick={() => {
            downloadWorkPdf({ progress, runs, predictions, reflections, studentId });
            notify('success', 'Work PDF downloaded.');
          }}
        >
          Save work as PDF
        </button>
      </div>
      <div className="sandbox-submit">
        <h2>Submit your work</h2>
        <p className="small">
          Upload your PDF through the course submission form.
        </p>
        <a
          className="btn primary"
          href="https://forms.gle/6rsriJ7ibgkAAopw5"
          target="_blank"
          rel="noopener noreferrer"
        >
          Open submission form
        </a>
      </div>
    </div>
  );
}

function Glossary() {
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) document.getElementById(hash)?.scrollIntoView({ block: 'start' });
  }, []);
  const back = useApp((s) => s.currentLessonId);
  return (
    <div className="lesson">
      <h1>Glossary</h1>
      <p className="lead">Every symbol and term used in the course, with units and an example.</p>
      <p>
        <a href={lessonPath(back)} onClick={(e) => (e.preventDefault(), navigate(lessonPath(back)))}>
          ← Back to Module {LESSON_BY_ID[back]?.number}
        </a>
      </p>
      <dl className="glossary">
        {GLOSSARY_LIST.map((g) => (
          <div key={g.id} id={`term-${g.id}`} className="glossary-entry" tabIndex={-1}>
            <dt>
              {g.tex && (
                <>
                  <MathTex tex={g.tex} />{' '}
                </>
              )}
              {g.label}
              {g.units && <span className="muted small"> ({parseMarkup(g.units, `${g.id}-u`)})</span>}
            </dt>
            <dd>
              <p>{parseMarkup(g.meaning, g.id)}</p>
              <p className="small">
                <strong>Example:</strong> {parseMarkup(g.example, `${g.id}-ex`)}
              </p>
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
